import { ImageMagick, initializeImageMagick, MagickFormat } from '@imagemagick/magick-wasm';

import { Asset } from '@rover/types/src/ImagePicker';

import { Logger } from '../modules/Logging';
import { MediaFile } from '../modules/Media';
import {
  emitMediaMetric,
  getElapsedBucket,
  getSizeBucket,
  getSizeDeltaBucket,
  MediaMetricNames,
} from '../modules/Media/metrics';

let $magickReady: Promise<void> | null = null;

export function getWasmUrl(): URL | undefined {
  if (typeof import.meta !== 'undefined' && import.meta.url) {
    return new URL('@imagemagick/magick-wasm/magick.wasm', import.meta.url);
  }
  return undefined;
}

export async function ensureMagick(): Promise<void> {
  if ($magickReady) return $magickReady;
  $magickReady = (async () => {
    // Loading the wasm binary happens once per page load, so it's timed apart from each transcode.
    const startedAt = Date.now();
    try {
      const wasmUrl = getWasmUrl();
      if (!wasmUrl) throw new Error('Failed to get WASM URL');
      const wasmBytes = await (await fetch(wasmUrl)).arrayBuffer();
      await initializeImageMagick(wasmBytes);
      emitMediaMetric(MediaMetricNames.MAGICK_INIT, {
        outcome: 'success',
        elapsed: getElapsedBucket(Date.now() - startedAt),
      });
    } catch (e) {
      emitMediaMetric(MediaMetricNames.MAGICK_INIT, {
        outcome: 'error',
        elapsed: getElapsedBucket(Date.now() - startedAt),
      });
      throw e;
    }
  })();
  return $magickReady;
}

function fileToByteArray(file: File | Asset): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const arrayBuffer = reader.result as ArrayBuffer;
      resolve(new Uint8Array(arrayBuffer));
    };
    reader.onerror = reject;

    // Handle both File and Asset types
    if (file instanceof File) {
      reader.readAsArrayBuffer(file);
    } else {
      // Asset type - convert to Blob first
      reader.readAsArrayBuffer(file as unknown as Blob);
    }
  });
}

export async function toJpeg(mediaFile: MediaFile, quality = 85): Promise<File> {
  await ensureMagick();

  const { file, name, size } = mediaFile;

  const startedAt = Date.now();
  let out: File | undefined;

  const emitOutcome = (outcome: 'success' | 'error', outputSize?: number): void =>
    emitMediaMetric(MediaMetricNames.TRANSCODE_COMPLETED, {
      transcoder: 'magick-wasm',
      outcome,
      // Shows whether the transcode made the file smaller or larger.
      sizeDelta: getSizeDeltaBucket(size, outputSize),
      sizeBefore: getSizeBucket(size),
      sizeAfter: getSizeBucket(outputSize),
      elapsed: getElapsedBucket(Date.now() - startedAt),
    });

  let bytes: Uint8Array;
  try {
    bytes = await fileToByteArray(file);
  } catch (e) {
    emitOutcome('error');
    throw e;
  }

  try {
    ImageMagick.read(bytes, (img) => {
      img.quality = quality; // eslint-disable-line no-param-reassign
      img.autoOrient(); // Ensure orientation is preserved
      img.strip();
      img.write(MagickFormat.Jpeg, (data) => {
        const ab = data.slice().buffer;
        out = new File([ab], name, { type: 'image/jpeg' });
      });
    });
  } catch (e) {
    emitOutcome('error');
    Logger.error(Error(`magick-wasm: toJpeg failed for file ${name}: ${e}`));
    throw e;
  }

  if (!out) {
    emitOutcome('error');
    throw new Error('magick-wasm: read/write did not produce output');
  }

  emitOutcome('success', out.size);

  return out;
}
